import React, { useEffect, useMemo } from 'react';
import { Tabs } from 'antd';
import { useImmer } from 'use-immer';
import EditorContainer from './EditorContainer';
import { RawDraftContentState } from 'draft-js';
import { CtaDataInput, CtaDataObject, NotificationTemplateVariable } from '@/graphql/generated/graphql';

const { TabPane } = Tabs;

type Props = {
  onChangeRichTemplateBody: ({
    messageTemplate,
    rawDraftContentState,
  }: {
    messageTemplate: string;
    rawDraftContentState: RawDraftContentState;
  }) => void;
  onChangeRichTemplateSubject: (content?: string) => void;
  content?: RawDraftContentState;
  subject?: string;
  variables?: NotificationTemplateVariable[];
  onAddCTAData: (data: CtaDataInput) => void;
  ctaDataList: Array<CtaDataObject>;
};

const TemplateTextEditor: React.FC<Props> = ({
  onChangeRichTemplateBody,
  onChangeRichTemplateSubject,
  content,
  subject,
  variables,
  onAddCTAData,
  ctaDataList,
}) => {
  const [state, setState] = useImmer({
    newTabIndex: 1,
    activeKey: 'variant_1',
    panes: new Map<string, { content: JSX.Element; tabName: string }>(),
  });

  const initTemplates = useMemo(() => {
    return new Map([
      [
        'variant_1',
        {
          content: (
            <EditorContainer
              onChangeRichTemplateBody={onChangeRichTemplateBody}
              onChangeRichTemplateSubject={onChangeRichTemplateSubject}
              onAddCTAData={onAddCTAData}
              content={content}
              subject={subject}
              variables={variables}
              ctaDataList={ctaDataList}
            />
          ),
          tabName: 'Variant 1',
        },
      ],
    ]);
  }, [content, ctaDataList, onAddCTAData, onChangeRichTemplateBody, onChangeRichTemplateSubject, subject, variables]);

  useEffect(() => {
    initTemplates.forEach((value, key) => {
      setState((draft) => {
        draft.panes.set(key, value);
      });
    });
  }, [initTemplates, setState]);

  const onChange = (activeKey: string) => {
    setState((draft) => {
      draft.activeKey = activeKey;
    });
  };

  const onEdit = (targetKey: React.MouseEvent | React.KeyboardEvent | string, action: 'add' | 'remove') => {
    if (action === 'remove') {
      remove(targetKey);
    } else add();
  };

  const add = () => {
    setState((draft) => {
      draft.panes.set(`variant_${draft.newTabIndex + 1}`, {
        content: (
          <EditorContainer
            variables={variables}
            onAddCTAData={onAddCTAData}
            subject={`Variant ${draft.newTabIndex + 1} title`}
            onChangeRichTemplateBody={onChangeRichTemplateBody}
            onChangeRichTemplateSubject={onChangeRichTemplateSubject}
            ctaDataList={ctaDataList}
          />
        ),
        tabName: `Variant ${draft.newTabIndex + 1}`,
      });
      draft.activeKey = `variant_${draft.newTabIndex + 1}`;
      draft.newTabIndex = draft.newTabIndex + 1;
    });
  };

  const remove = (targetKey: React.MouseEvent | React.KeyboardEvent | string) => {
    if (state.panes.size > 1) {
      const key = targetKey as string;
      setState((draft) => {
        draft.panes.delete(key);
        draft.activeKey = draft.panes.keys().next().value as string;
      });
    }
    return;
  };

  const renderTemplateVariants = () => {
    const variants = [];

    for (const [key, value] of state.panes) {
      variants.push(
        <TabPane tab={value.tabName} key={key}>
          {value.content}
        </TabPane>
      );
    }

    return variants;
  };

  return (
    <Tabs type="editable-card" onChange={onChange} activeKey={state.activeKey} onEdit={onEdit}>
      {renderTemplateVariants()}
    </Tabs>
  );
};

export default TemplateTextEditor;
